import { Nullable } from '@app/types';
import {
  NotificationLevel,
  WebhookAuthType,
  WebhookEvents,
} from '@const/consts';
import { IEnvelopeUser } from '../envelopes/types';

export interface IBoxesInitialValues {
  webHook: ICurrentWebHook;
  boxes: IBox[];
}

export interface IWebHook {
  isWebhookEnabled: boolean;
  webhookUrl: Nullable<string>;
  webhookAuthType: Nullable<WebhookAuthType>;
  webhookAuthToken: Nullable<string>;
  webhookEvents: Nullable<Array<Nullable<WebhookEvents>>>;
}

export interface ICurrentWebHook extends IWebHook {
  lastCallInfo: ILastCallInfo;
}

export interface ILastCallInfo {
  timestamp: string;
  statusCode: string;
  statusMessage: Nullable<string>;
}

// Роль пользователя в ящике
export enum IBoxRole {
  VIEWER = 'VIEWER',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
}

export enum UserBoxAccessRole {
  VIEWER = 'VIEWER',
  MANAGER = 'MANAGER',
}

// Роль пользователя в ящике
export interface IBoxMain {
  /** Электронная почта */
  email: string;
  /** Наименование организации */
  companyName: string;
  /** Почтовый индекс */
  companyZip?: Nullable<string>;
  /** Почтовый индекс */
  companyAddress?: Nullable<string>;
}

// Интерфейс для создания ящика
export interface IBoxCreate extends IBoxMain {
  position?: Nullable<string>;
}

// Интерфейс для создания ящика
export interface IBox extends IBoxMain {
  /** Ящик принадлежит текущему пользователю */
  isPersonal: boolean;
  /** oguid бокса */
  oguid: string;
  /** Роль пользователя */
  role: IBoxRole;
  /** Флаг доступа к ящику только на чтение */
  isReadOnlyEmail: boolean;
  /** Дата и время создания */
  created?: Nullable<string>;
  /** Краткая структура с пользователем */
  createdBy?: IEnvelopeUser;
  /** Количество активированных пользователей в ящике */
  usersActivated?: number;
  /** Количество пользователей в ящике, ожидающий активации */
  usersWaiting?: number;
  /** Лого текущего бокса */
  logo?: Nullable<string>;
}

// Интерфейс данных для обновления ящика
export type IBoxUpdateData = Required<IBoxMain>;

// Интерфейс для запроса обновления ящика
export interface IBoxUpdate {
  boxOguid: string;
  data: IBoxUpdateData;
}

// Интерфейс обновления лого ящика
export interface IBoxLogoUpdate {
  boxOguid: string;
  data: FormData;
}

// Интерфейс создания инвайта пользователя
export interface IUserInviteCreate {
  lastName?: Nullable<string>;
  firstName?: Nullable<string>;
  middleName?: Nullable<string>;
  fullName: string;
  initials?: Nullable<string>;
  email?: Nullable<string>;
  position?: Nullable<string>;
  locale: string;
  timeZone: string;
  subdivisionOguid: Nullable<string>;
  incomingRole: UserBoxAccessRole;
  outgoingRole: UserBoxAccessRole;
  envelopeVisibility: UserBoxEnvelopeVisibility;
  subdivisionAccessScopeOguids: Nullable<string[]>;
  isAdministrator: boolean;
  phone: Nullable<string>;
}

// Интерфейс запроса создания инвайта пользователя
export type IUserInviteCreateRequest = Omit<IUserInviteCreate, 'phone'> & {
  phone?: Nullable<number>;
};

// Интерфейс обновления инвайта пользователя
export type IUserInviteUpdate = Omit<
  Required<IUserInviteCreateRequest>,
  'email' | 'phone'
>;

export interface IUserNotifications {
  isEmailNotification: boolean;
  notificationLevel: NotificationLevel;
}

export interface IUserUpdate extends IUserNotifications {
  position?: Nullable<string>;
  subdivisionOguid: Nullable<string>;
  incomingRole: UserBoxAccessRole;
  outgoingRole: UserBoxAccessRole;
  envelopeVisibility: UserBoxEnvelopeVisibility;
  subdivisionAccessScopeOguids: Nullable<string[]>;
  isAdministrator: boolean;
}

/** Краткая структура активного участника ящика */
export interface IBoxMember {
  userBoxOguid: string;
  email?: Nullable<string>;
  phone?: Nullable<string | number>;
  fullName: string;
  position: Nullable<string>;
}

// Интерфейс пользователя в бизнес-ящике (get)
export interface IUser extends IUserNotifications {
  lastName: Nullable<string>;
  firstName: Nullable<string>;
  middleName: Nullable<string>;
  fullName: string;
  initials: Nullable<string>;
  email?: Nullable<string>;
  position: Nullable<string>;
  locale: string;
  timeZone: string;
  phone?: Nullable<number>;
  created: string;
  userBoxOguid: string;
  createdBy: IEnvelopeUser;
  incomingRole: UserBoxAccessRole;
  outgoingRole: UserBoxAccessRole;
  envelopeVisibility: UserBoxEnvelopeVisibility;
  subdivisionAccessScope: Nullable<IUserBoxSubdivisionShort[]>;
  isAdministrator: boolean;
  subdivision: Nullable<IUserBoxSubdivisionShort>;
  role?: IBoxRole;
  incomingEnvelopeVisibility?: UserBoxEnvelopeVisibility;
  incomingVisibilitySubdivisions?: Nullable<IUserBoxSubdivisionShort[]>;
  outgoingEnvelopeVisibility?: UserBoxEnvelopeVisibility;
  outgoingVisibilitySubdivisions?: Nullable<IUserBoxSubdivisionShort[]>;
}

// Интерфейс пользователя в бизнес-ящике (для формы)
export type IUserForm = Omit<IUser, 'phone'> & {
  phone?: Nullable<string>;
  subdivisionOguid: Nullable<string>;
  subdivisionAccessScopeOguids: Nullable<string[]>;
}

// Интерфейс приглашения пользователя в бизнес-ящик
export interface IUserInvite extends Omit<IUser, 'userBoxOguid' | 'isEmailNotification' | 'notificationLevel'> {
  inviteOguid: string;
  userBoxOguid?: string;
  notificationDatetime: string;
  notificationCount: number;
}

export interface IUserInviteResend {
  invites: string[];
}

/** Интерфейс обновления лого в стейте */
export interface ILogoUpd {
  boxOguid: string;
  logo: Nullable<string>;
}

export enum EnvelopeFieldType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  CURRENCY = 'CURRENCY',
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  BOOL = 'BOOL',
  TABLE = 'TABLE',
  SELECT = 'SELECT',
}

export const isNumericEnvelopeFieldType = (type?: Nullable<EnvelopeFieldType>): boolean => (
  type === EnvelopeFieldType.NUMBER || type === EnvelopeFieldType.CURRENCY
);

export enum EnvelopeFieldDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  BOTH = 'BOTH',
}

export interface IEnvelopeTableColumn {
  name: string;
  systemKey: string;
  oldKey?: Nullable<string>;
  type: EnvelopeFieldScalarType;
  decimalPlaces: Nullable<number>;
  currency: Nullable<string>;
  envelopeDirection?: EnvelopeFieldDirection;
}

export type EnvelopeFieldScalarType = Exclude<EnvelopeFieldType, EnvelopeFieldType.TABLE | EnvelopeFieldType.SELECT>;

export interface IEnvelopeSelectColumn {
  name: string;
  systemKey: string;
  type: EnvelopeFieldScalarType;
  decimalPlaces: Nullable<number>;
  currency: Nullable<string>;
  isSearchable?: boolean;
  isVisibleInPicker: boolean;
  isVisibleAfterSelection: boolean;
}

export type IEnvelopeSelectValueFields = Record<string, boolean | number | string | null>;

export interface IEnvelopeSelectValue {
  key?: string;
  fields: IEnvelopeSelectValueFields;
}

export interface IEnvelopeSelectResolvedValue {
  key: string;
  fields: Nullable<IEnvelopeSelectValueFields>;
}

export type IEnvelopeFieldRuntimeScalarValue = boolean | number | string | null;
export type IEnvelopeFieldRuntimeTableValue = Array<Record<string, IEnvelopeFieldRuntimeScalarValue>>;
export type IEnvelopeFieldRuntimeValue =
  | IEnvelopeFieldRuntimeScalarValue
  | IEnvelopeFieldRuntimeTableValue
  | IEnvelopeSelectResolvedValue;

export interface IEnvelopeField {
  name: string;
  key?: string;
  systemKey: string;
  type: EnvelopeFieldType;
  decimalPlaces: Nullable<number>;
  currency: Nullable<string>;
  envelopeDirection: EnvelopeFieldDirection;
  isSearchable?: boolean;
  isVisibleInListView: boolean;
  description: Nullable<string>;
  tableColumns: Nullable<IEnvelopeTableColumn[]>;
  selectColumns: Nullable<IEnvelopeSelectColumn[]>;
  keyColumn: Nullable<string>;
  displayTemplate: Nullable<string>;
  displayTemplateColumnKeys: Nullable<string[]>;
  maxValuesToReturn: Nullable<number>;
  isClientSideProcessing: Nullable<boolean>;
}

export type IEnvelopeFieldCreate = IEnvelopeField;

export type IEnvelopeFieldUpdate = Pick<
  IEnvelopeField,
  | 'name'
  | 'systemKey'
  | 'currency'
  | 'envelopeDirection'
  | 'isVisibleInListView'
  | 'description'
  | 'tableColumns'
  | 'selectColumns'
  | 'keyColumn'
  | 'displayTemplate'
  | 'maxValuesToReturn'
  | 'isClientSideProcessing'
>;

export interface IEnvelopeFieldReorder {
  systemKeys: string[];
}

export interface ISubdivision {
  oguid: string;
  name: string;
  description: Nullable<string>;
  totalUserCount?: number;
}

export interface ISubdivisionSave {
  name: string;
  description: Nullable<string>;
}

export interface IContactDirectoryEntry {
  oguid: string;
  key: Nullable<string>;
  fullName: Nullable<string>;
  email: Nullable<string>;
  phone: Nullable<string>;
  companyName: Nullable<string>;
}

export type IContactDirectorySave = Omit<IContactDirectoryEntry, 'oguid'>;

export interface IUserBoxSubdivisionShort {
  oguid: string;
  name: string;
}

export enum UserBoxEnvelopeVisibility {
  ONLY_MY_ENVELOPES = 'ONLY_MY_ENVELOPES',
  ALL_ENVELOPES = 'ALL_ENVELOPES',
  ONLY_MY_SUBDIVISION_ENVELOPES = 'ONLY_MY_SUBDIVISION_ENVELOPES',
  SELECTED_SUBDIVISIONS_ENVELOPES = 'SELECTED_SUBDIVISIONS_ENVELOPES',
}

export enum EnvelopeTemplateStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
}

export enum EnvelopeTemplateFileSource {
  SHELF = 'SHELF',
  ENVELOPE_TEMPLATE = 'ENVELOPE_TEMPLATE',
}

export enum EnvelopeTemplatePlacementMode {
  ABSOLUTE = 'ABSOLUTE',
  RELATIVE = 'RELATIVE',
}

export enum EnvelopeTemplatePageSearchMode {
  ALL_PAGES = 'ALL_PAGES',
  SELECTED_PAGES = 'SELECTED_PAGES',
  PAGE_RANGE = 'PAGE_RANGE',
}

export enum EnvelopeTemplateAnchorOccurrenceMode {
  FIRST = 'FIRST',
  LAST = 'LAST',
  NUMBER = 'NUMBER',
}

export enum EnvelopeTemplateXPlacement {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
  RIGHT = 'RIGHT',
}

export enum EnvelopeTemplateYPlacement {
  TOP = 'TOP',
  CENTER = 'CENTER',
  BOTTOM = 'BOTTOM',
}

export interface IEnvelopeTemplateTopLevelField {
  systemKey: string;
  key?: string;
  isCheckUnique?: Nullable<boolean>;
  isRequired: boolean;
  isDeleted?: boolean;
  isOnSameLineAsPrevious: boolean;
  hasHorizontalSeparatorBefore: boolean;
  editableWhen: Nullable<IEnvelopeTemplateFieldEditableWhen>;
}

export interface IEnvelopeTemplateVariable {
  key: string;
  type: EnvelopeFieldType;
}

export interface IEnvelopeTemplateFile {
  oguid?: string;
  fileOguid?: string;
  fileName?: Nullable<string>;
  fileMustBeProvidedByUser: boolean;
  source?: EnvelopeTemplateFileSource;
}

export interface IEnvelopeTemplateStartCondition {
  fieldSystemKey: string;
  fieldKey?: string;
  isDeleted?: boolean;
  operator: string;
  value: Nullable<string | number | boolean | Array<string | number | boolean>>;
}

export enum EnvelopeTemplateEditableWhenGroupOperator {
  AND = 'and',
  OR = 'or',
}

export interface IEnvelopeTemplateFieldEditableWhenCondition {
  fieldSystemKey: string;
  fieldKey?: string;
  isDeleted?: boolean;
  operator: string;
  value: Nullable<string | number | boolean | Array<string | number | boolean>>;
}

export interface IEnvelopeTemplateFieldEditableWhen {
  operator: EnvelopeTemplateEditableWhenGroupOperator;
  conditions: IEnvelopeTemplateFieldEditableWhenCondition[];
}

export type ContactDirectoryLookupKey = 'key' | 'fullName' | 'email' | 'phone' | 'companyName';

export type ContactDirectoryResolveOperation = 'eq' | 'in' | 'like' | '%like' | 'like%';

export interface IContactDirectoryResolveRecipientRule {
  lookupFieldKey: string;
  contactDirectoryLookupKey: ContactDirectoryLookupKey;
  operation: ContactDirectoryResolveOperation;
}

export interface IEnvelopeTemplatePrepositionedMark {
  fileIndex: number;
  key?: Nullable<string>;
  isLocked?: boolean;
  placementMode?: EnvelopeTemplatePlacementMode;
  pageSearchMode?: EnvelopeTemplatePageSearchMode;
  pageSearchPages?: Nullable<string>;
  pageSearchFromPage?: Nullable<number>;
  pageSearchToPage?: Nullable<number>;
  pageNumber?: number;
  anchorText?: Nullable<string>;
  anchorOccurrenceMode?: EnvelopeTemplateAnchorOccurrenceMode;
  anchorOccurrenceNumber?: Nullable<number>;
  xPlacement?: EnvelopeTemplateXPlacement;
  xOffset?: Nullable<number>;
  yPlacement?: EnvelopeTemplateYPlacement;
  yOffset?: Nullable<number>;
  topLeftX?: Nullable<number>;
  topLeftY?: Nullable<number>;
  height?: Nullable<number>;
  width?: Nullable<number>;
  type: string;
  isReadOnly?: Nullable<boolean>;
  isRequired?: Nullable<boolean>;
  valueList?: Nullable<string[]>;
  value?: Nullable<string>;
  fontSize?: Nullable<number>;
  fontType?: Nullable<string>;
  font?: Nullable<string>;
  color?: Nullable<string>;
  scale?: Nullable<number>;
  tooltip?: Nullable<string>;
  label?: Nullable<string>;
}

export interface ILabelGroup {
  key: string;
  name: string;
}

export interface ILabel {
  fullKey: string;
  key: string;
  group: Nullable<ILabelGroup>;
  name: string;
  color: Nullable<string>;
}

export interface IEnvelopeTemplateLabel extends ILabel {
  isDeleted: boolean;
}

export interface ILabelSave {
  key: string;
  name: string;
  color: Nullable<string>;
}

export interface ILabelCreate extends ILabelSave {
  groupKey: Nullable<string>;
}

export interface IRouteNotificationSubscriber {
  type: 'RECIPIENT' | 'SENDER' | 'BOX_USER';
  recipientKey: Nullable<string>;
  boxUserOguid: Nullable<string>;
}

export interface IRouteNotificationSubscription {
  subscriber: IRouteNotificationSubscriber;
  events: Nullable<string[]>;
  notifyOnFinished: boolean;
}

export interface IEnvelopeRouteNotifications {
  subscriptions: Nullable<IRouteNotificationSubscription[]>;
}

export interface IEnvelopeTemplateRecipient {
  recipientKey?: Nullable<string>;
  type: string;
  assignmentDescription: Nullable<string>;
  resolveRecipientRule?: Nullable<IContactDirectoryResolveRecipientRule>;
  boxId: Nullable<string>;
  assignedToBoxUserOguid: Nullable<string>;
  isAssignedToBoxUser: boolean;
  isNoPredefinedUser: boolean;
  isHostMode: Nullable<boolean>;
  guestEmail: Nullable<string>;
  signer: Nullable<{
    fullName: string;
    initials: Nullable<string>;
    position: Nullable<string>;
    phone: Nullable<string | number>;
  }>;
  signingParams: Nullable<{
    isAllowElectronicSignature: boolean;
    isSignOnBehalfOfOrganization?: Nullable<boolean>;
    signerType: string;
  }>;
  isRequireSelfie: boolean;
  isRequireSms: boolean;
  isSignOnBehalfOfOrganization?: Nullable<boolean>;
  prepositionedMarks: Nullable<IEnvelopeTemplatePrepositionedMark[]>;
  startConditions: Nullable<IEnvelopeTemplateStartCondition[]>;
}

export interface IEnvelopeTemplateFlowStage {
  stageKey: Nullable<string>;
  labels: string[];
  description: Nullable<string>;
  recipients: IEnvelopeTemplateRecipient[];
}

export interface IEnvelopeTemplate {
  oguid: string;
  name: string;
  description: Nullable<string>;
  clientScript: Nullable<string>;
  status: EnvelopeTemplateStatus;
  icon: Nullable<number>;
  isPreprocessingEnabled: boolean;
  isTemplateLocked: boolean;
  labels: IEnvelopeTemplateLabel[];
  fields: Nullable<IEnvelopeTemplateTopLevelField[]>;
  variables: Nullable<IEnvelopeTemplateVariable[]>;
  flow: Nullable<IEnvelopeTemplateFlowStage[]>;
  notifications: Nullable<IEnvelopeRouteNotifications>;
  files: Nullable<IEnvelopeTemplateFile[]>;
  updatedDateTime?: string;
  updatedBy?: Nullable<IEnvelopeUser>;
}

export type IEnvelopeTemplateSave = Omit<
  IEnvelopeTemplate,
  'oguid' | 'labels' | 'updatedDateTime' | 'updatedBy'
> & {
  labels: string[];
};

export interface IEnvelopeTemplateReorder {
  oguids: string[];
}

export interface IEnvelopeTemplateMarkResolveRequest {
  envelopeTemplateOguid?: Nullable<string>;
  files: Array<{
    source: EnvelopeTemplateFileSource;
    oguid: string;
  }>;
  prepositionedMarks: IEnvelopeTemplatePrepositionedMark[];
}

export interface IEnvelopeTemplateMarkResolveResponse {
  prepositionedMarks: IEnvelopeTemplatePrepositionedMark[];
}

export interface IEnvelopeTemplateGenerateDocumentsRequest {
  fields: Nullable<Record<string, unknown>>;
}

export interface IEnvelopeTemplateGeneratedDocument {
  oguid: string;
  fileName: string;
  mimeType: string;
  size: number;
}

export type IEnvelopeTemplateGenerateDocumentsResponse = IEnvelopeTemplateGeneratedDocument[];

export interface IEnvelopeTemplatePreviewWorkflowRequest {
  fields: Nullable<Record<string, unknown>>;
  variables?: Nullable<Record<string, unknown>>;
  files?: Nullable<string[]>;
}

export interface IEnvelopeTemplatePreviewWorkflowResponse {
  flow: IEnvelopeTemplateFlowStage[];
}

export enum PreProcessingJobStatus {
  WAITING_FOR_REPLY = 'WAITING_FOR_REPLY',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  IN_QUEUE = 'IN_QUEUE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  STOPPED = 'STOPPED',
}

export interface IEnvelopeTemplatePreProcessingJobPostRequest {
  fileOguid: string;
}

export interface IEnvelopeTemplatePreProcessingJobResponse {
  oguid: string;
  status: PreProcessingJobStatus;
  errorCode: Nullable<string>;
  errorDescription: Nullable<string>;
  time: Nullable<number>;
  fields: Nullable<Record<string, IEnvelopeFieldRuntimeValue>>;
  preliminaryCost: Nullable<number>;
}
